import { AiMessageSteps, AiMessageStepStatusEnum } from "__generated__/graphql"

/**
 * A Skills turn is not one request/response pair like the rest of the pipeline: the agent runs a
 * bounded decide → act → observe loop over the author's own instruction document, so the log only
 * makes sense as "which instruction was active, what did it decide there, what came back".
 * This rebuilds that shape from the flat audit steps.
 */
export const SKILL_STEP_NAMES = ["SKILL_ROUTING", "SKILL_AGENT"]

export type SkillInstructionLine = { id: string; text: string }

export type SkillUsage = {
  inputToken: number
  outputToken: number
  amount: number
}

export type SkillCycle = {
  stepId: string
  cycle?: number
  command: string
  rationale?: string
  durationMs: number
  failed: boolean
  instructions: SkillInstructionLine[]
  capability?: { kind?: string; label?: string }
  stepOutcome?: string
  waitForUser: boolean
  /** Customer-facing text the cycle produced, when it wrote one. */
  response?: string
  action?: { name?: string; result?: unknown }
  usage?: SkillUsage
  request: unknown
  rawResponse: unknown
}

export type SkillStepGroup = {
  id: string
  title: string
  order?: number
  cycles: SkillCycle[]
  completed: boolean
}

export type SkillOutcome = {
  label: string
  tone: "success" | "warning" | "error" | "info" | "default"
  detail?: string
}

export type SkillTrigger = {
  confidence?: number
  reason?: string
  decision?: string
}

export type SkillRun = {
  runId: string
  skillName?: string
  stepCount?: number
  trigger?: SkillTrigger
  groups: SkillStepGroup[]
  outcome: SkillOutcome
  durationMs: number
  usage: SkillUsage
  /** The last step the run reached, and how it left it. */
  endedAt?: { groupId: string; reason: SkillEndReason }
}

/**
 * `finished` means the run deliberately closed the turn there (handed back, handed off, ended),
 * `waiting` that it answered and expects the customer, `stopped` that it simply ran out of the
 * step without finishing it — the case worth looking into.
 */
export type SkillEndReason = "finished" | "waiting" | "stopped" | "error"

export type SkillTimeline = {
  runs: SkillRun[]
  /** A trigger decision that never opened a run: nothing matched, so no Skill answered. */
  skippedTrigger?: SkillTrigger
}

/** `@Sipariş getir{ACTION:cap-1}` — the chip form the runtime writes into an instruction line. */
const CAPABILITY_TOKEN = /@([^{]+)\{([A-Z_]+):([^}]*)\}/g

function asObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asText(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined
}

function asCount(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined
}

function readUsage(
  response: Record<string, unknown> | null
): SkillUsage | undefined {
  const usage = asObject(response?.usage)
  if (!usage) return undefined

  return {
    inputToken: asCount(usage.inputToken) ?? 0,
    outputToken: asCount(usage.outputToken) ?? 0,
    amount: asCount(usage.amount) ?? 0
  }
}

function readInstructions(
  context: Record<string, unknown> | null
): SkillInstructionLine[] {
  const instructions = context?.instructions
  if (!Array.isArray(instructions)) return []

  return instructions
    .map((item) => {
      const line = asObject(item)
      const text = asText(line?.text)
      return text ? { id: asText(line?.id) ?? text, text } : null
    })
    .filter((line): line is SkillInstructionLine => !!line)
}

function emptyUsage(): SkillUsage {
  return { inputToken: 0, outputToken: 0, amount: 0 }
}

function addUsage(total: SkillUsage, usage?: SkillUsage): SkillUsage {
  if (!usage) return total

  return {
    inputToken: total.inputToken + usage.inputToken,
    outputToken: total.outputToken + usage.outputToken,
    amount: total.amount + usage.amount
  }
}

const OUTCOMES: Record<string, SkillOutcome> = {
  RESPOND: { label: "Müşteriye yanıt verdi", tone: "success" },
  COMPLETE: { label: "Skill tamamlandı", tone: "success" },
  END: { label: "Skill tamamlandı", tone: "success" },
  HANDOFF_TEAM: { label: "Takıma aktarıldı", tone: "warning" },
  HANDOFF_CPERSON: { label: "Temsilciye aktarıldı", tone: "warning" },
  RETURN_TO_QPIEN_AI: { label: "Qpien AI devraldı", tone: "info" },
  RUN_SKILL: { label: "Alt skill çalıştırdı", tone: "info" },
  SWITCH_SKILL: { label: "Başka skill'e geçti", tone: "info" },
  NO_RESPONSE: { label: "Yanıt üretmedi", tone: "default" },
  CALL_ACTION: { label: "Aksiyonda kaldı", tone: "warning" },
  SEARCH_KNOWLEDGE: { label: "Bilgi aramasında kaldı", tone: "warning" }
}

function buildOutcome(run: SkillRun, failure?: string): SkillOutcome {
  if (failure) {
    return { label: "Çalışma hatası", tone: "error", detail: failure }
  }

  const cycles = run.groups.flatMap((group) => group.cycles)
  const last = cycles[cycles.length - 1]
  if (!last) {
    return { label: "Komut üretilmedi", tone: "default" }
  }

  const outcome = OUTCOMES[last.command] ?? {
    label: last.command,
    tone: "default" as const
  }
  if (last.command === "RESPOND" && last.waitForUser) {
    return { label: "Yanıt verdi, müşteri yanıtı bekleniyor", tone: "info" }
  }

  return outcome
}

/** Commands that close the turn on purpose, rather than leaving the step half-done. */
const TERMINAL_COMMANDS = [
  "COMPLETE",
  "END",
  "RETURN_TO_QPIEN_AI",
  "HANDOFF_TEAM",
  "HANDOFF_CPERSON",
  "SWITCH_SKILL",
  "RUN_SKILL"
]

function readEndOfRun(
  run: SkillRun,
  failed: boolean
): { groupId: string; reason: SkillEndReason } | undefined {
  const lastGroup = run.groups[run.groups.length - 1]
  if (!lastGroup) return undefined

  const lastCycle = lastGroup.cycles[lastGroup.cycles.length - 1]
  if (failed) return { groupId: lastGroup.id, reason: "error" }
  if (lastCycle?.waitForUser)
    return { groupId: lastGroup.id, reason: "waiting" }
  if (TERMINAL_COMMANDS.includes(lastCycle?.command ?? "")) {
    return { groupId: lastGroup.id, reason: "finished" }
  }

  return {
    groupId: lastGroup.id,
    reason: lastGroup.completed ? "finished" : "stopped"
  }
}

type CycleDraft = SkillCycle & { stepTitle?: string; stepOrder?: number }

/**
 * Cycles are grouped by the step that was active while they ran, in the order the run reached
 * them. A step counts as done once a cycle inside it reports COMPLETED; the last group that never
 * did is where the run stopped.
 */
function groupCycles(cycles: CycleDraft[]): SkillStepGroup[] {
  const groups: SkillStepGroup[] = []

  cycles.forEach((cycle) => {
    const id = cycle.stepId || "-"
    const current = groups[groups.length - 1]
    if (current && current.id === id) {
      current.cycles.push(cycle)
      current.completed = current.completed || cycle.stepOutcome === "COMPLETED"
      return
    }

    groups.push({
      id,
      title: cycle.stepTitle || id,
      order: cycle.stepOrder,
      cycles: [cycle],
      completed: cycle.stepOutcome === "COMPLETED"
    })
  })

  return groups
}

/**
 * Every anchor on the acted-on path is logged, including the step heading itself and a line that
 * holds nothing but the capability chip. Both are already on screen — as the step title and as the
 * action — so showing them again only buries the instruction that actually matters.
 */
function meaningfulInstructions(
  lines: SkillInstructionLine[],
  stepTitle?: string,
  hasCapability?: boolean
): SkillInstructionLine[] {
  return lines.filter((line) => {
    const withoutChips = line.text
      .replace(new RegExp(CAPABILITY_TOKEN), "")
      .trim()
    if (hasCapability && !withoutChips) return false
    if (stepTitle && line.text.trim() === stepTitle.trim()) return false

    return true
  })
}

function readCycle(step: AiMessageSteps): CycleDraft | null {
  const response = asObject(step.response)
  const request = asObject(step.request)
  const output = asObject(response?.output)
  const context = asObject(response?.skillContext)
  const command = asText(response?.command)
  if (!command) return null

  const anchorIds = Array.isArray(response?.instructionAnchorIds)
    ? (response?.instructionAnchorIds as unknown[]).map(String)
    : []
  const capability = asObject(context?.capability)
  const stepTitle = asText(context?.stepTitle)
  const instructions = meaningfulInstructions(
    readInstructions(context),
    stepTitle,
    !!capability
  )

  return {
    stepId: asText(output?.activeStepId) ?? "-",
    stepTitle,
    stepOrder: asCount(context?.stepOrder),
    cycle: asCount(context?.cycle),
    command,
    rationale: asText(step.reason) ?? undefined,
    durationMs: step.durationMs ?? 0,
    failed: step.status === AiMessageStepStatusEnum.Failed,
    // Older logs carry only the anchor ids; show those rather than nothing.
    instructions: instructions.length
      ? instructions
      : anchorIds.map((id) => ({ id, text: id })),
    capability: asObject(context?.capability)
      ? {
          kind: asText(asObject(context?.capability)?.kind),
          label: asText(asObject(context?.capability)?.label)
        }
      : undefined,
    stepOutcome: asText(output?.stepOutcome),
    waitForUser: output?.waitForUser === true,
    response: asText(output?.response),
    action:
      command === "CALL_ACTION" || command === "SEARCH_KNOWLEDGE"
        ? {
            name:
              asText(output?.actionName) ??
              asText(asObject(context?.capability)?.label) ??
              asText(request?.referenceId),
            result: output?.result
          }
        : undefined,
    usage: readUsage(response),
    request: step.request,
    rawResponse: step.response
  }
}

export function buildSkillTimeline(
  steps: AiMessageSteps[] | undefined
): SkillTimeline {
  const runs = new Map<string, SkillRun>()
  const order: string[] = []
  const cyclesByRun = new Map<string, CycleDraft[]>()
  const failureByRun = new Map<string, string>()
  let pendingTrigger: SkillTrigger | undefined
  let triggerUsed = false

  const ensureRun = (runId: string): SkillRun => {
    const existing = runs.get(runId)
    if (existing) return existing

    const created: SkillRun = {
      runId,
      groups: [],
      outcome: { label: "-", tone: "default" },
      durationMs: 0,
      usage: emptyUsage()
    }
    runs.set(runId, created)
    order.push(runId)
    if (pendingTrigger && !triggerUsed) {
      created.trigger = pendingTrigger
      triggerUsed = true
    }
    return created
  }

  ;[...(steps ?? [])]
    .sort((a, b) => (a.stepList ?? 0) - (b.stepList ?? 0))
    .forEach((step) => {
      const name = String(step.step)
      if (!SKILL_STEP_NAMES.includes(name)) return

      const response = asObject(step.response)
      const runId = asText(response?.skillRunId)

      if (name === "SKILL_ROUTING") {
        const decision = asText(response?.decision)
        if (decision) {
          pendingTrigger = {
            decision,
            confidence: asCount(response?.confidence),
            reason: asText(response?.reason) ?? asText(step.reason)
          }
          return
        }

        if (!runId) return

        const run = ensureRun(runId)
        run.skillName = run.skillName ?? asText(response?.skillName)
        if (step.status === AiMessageStepStatusEnum.Failed) {
          failureByRun.set(
            runId,
            asText(step.reason) ?? asText(step.summary) ?? "Bilinmeyen hata"
          )
        }
        return
      }

      if (!runId) return

      const cycle = readCycle(step)
      if (!cycle) return

      const run = ensureRun(runId)
      run.skillName =
        run.skillName ?? asText(asObject(response?.skillContext)?.skillName)
      run.stepCount =
        run.stepCount ?? asCount(asObject(response?.skillContext)?.stepCount)
      run.durationMs += cycle.durationMs
      run.usage = addUsage(run.usage, cycle.usage)
      if (cycle.failed) {
        failureByRun.set(runId, cycle.rationale ?? "Bilinmeyen hata")
      }

      const list = cyclesByRun.get(runId) ?? []
      list.push(cycle)
      cyclesByRun.set(runId, list)
    })

  const built = order.map((runId) => {
    const run = runs.get(runId) as SkillRun
    run.groups = groupCycles(cyclesByRun.get(runId) ?? [])
    run.outcome = buildOutcome(run, failureByRun.get(runId))
    run.endedAt = readEndOfRun(run, !!failureByRun.get(runId))
    return run
  })

  return {
    runs: built,
    skippedTrigger: !triggerUsed ? pendingTrigger : undefined
  }
}

export type InstructionToken =
  | { type: "text"; value: string }
  | { type: "capability"; label: string; kind: string }

export function tokenizeInstruction(text: string): InstructionToken[] {
  const tokens: InstructionToken[] = []
  const pattern = new RegExp(CAPABILITY_TOKEN)
  let lastIndex = 0
  let match = pattern.exec(text)

  while (match) {
    if (match.index > lastIndex) {
      tokens.push({ type: "text", value: text.slice(lastIndex, match.index) })
    }
    tokens.push({ type: "capability", label: match[1].trim(), kind: match[2] })
    lastIndex = match.index + match[0].length
    match = pattern.exec(text)
  }

  if (lastIndex < text.length) {
    tokens.push({ type: "text", value: text.slice(lastIndex) })
  }

  return tokens
}

/** One line that says what an action returned, without unfolding the whole payload. */
export function summarizeResult(result: unknown): string | undefined {
  if (result === null || result === undefined) return undefined
  if (typeof result === "string") return result.slice(0, 200)
  if (typeof result === "number" || typeof result === "boolean")
    return String(result)

  if (Array.isArray(result)) {
    return `${result.length} kayıt`
  }

  const record = asObject(result)
  if (!record) return undefined

  const named = [
    "title",
    "name",
    "productName",
    "orderNumber",
    "status",
    "price"
  ]
    .map((key) =>
      record[key] === undefined ? null : `${key}: ${String(record[key])}`
    )
    .filter((part): part is string => !!part)

  if (named.length) return named.join(" · ").slice(0, 200)

  return `${Object.keys(record).length} alan`
}
